import { BorderRadius, colors, eyebrow, mediaQueries, paragraph } from '@theme/lib';
import { FormEvent, ReactElement, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

type StyledToggleButtonProps = {
  isOpen: boolean;
};

export const StyledToggleButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== "isOpen"
})<StyledToggleButtonProps>`
  ${paragraph}
  display: block;
  background: ${(props) => props.theme.colors.button.secondary.bg};
  border: 1px solid ${(props) => props.theme.key === 'zsl' ? props.theme.colors.primary : colors.zooFont};
  border-radius: ${props => props.theme.config.buttonRadius};
  padding: 0.875rem 2.5rem 0.75rem 1rem;
  cursor: pointer;
  position: relative;
  text-align: left;
  width: 100%;
  box-sizing: border-box;

  &::after {
    content: '';
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-40%);
    height: 0.7rem;
    width: 0.875rem;
    background-position: center;
    background-repeat: no-repeat;
    background-image: ${(props) => `url('/Filter/arrow-${props.isOpen ? 'up' : 'down'}.svg')`};
  }

  ${mediaQueries.md} {
    min-width: 12rem;
    width: auto;
  }
`;

type StyledFormProps = {
  isOpen: boolean;
};

const StyledForm = styled.form<StyledFormProps>`
  background: ${colors.white};
  ${props => BorderRadius(props.theme.config.buttonRadius)}
  display: ${(props) => (props.isOpen ? 'flex' : 'none')};
  flex-direction: column;
  gap: 1rem;
  padding-top: 1rem;
  margin: 0.75rem 0 0.5rem;

  ${mediaQueries.md} {
    display: ${(props) => (props.isOpen ? 'inline-flex' : 'none')};
    min-width: 20.25rem;
    width: auto;
    position: absolute;
    z-index: 990;
  }
`;

const StyledCheckbox = styled.input`
  -webkit-appearance: none;
  appearance: none;
  background-color: none;
  margin: 0;
  height: 1.5rem;
  width: 1.5rem;
  border: 1px solid ${(props) => props.theme.colors.primary};
  border-radius: 0.25rem;
  cursor: pointer;

  &:checked {
    background-color: ${(props) => props.theme.colors.primary};
    background-image: url(/Filter/tick.svg);
    background-position: center;
    background-repeat: no-repeat;
  }
`;

const StyledLabel = styled.label`
  ${paragraph}
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0 1rem;
  cursor: pointer;
`;

const StyledApplyButton = styled.button`
  ${eyebrow}
  cursor: pointer;
  background-color: ${(props) => props.theme.colors.primary};
  color: ${(props) => props.theme.colors.button.primary.text};
  padding: 0.875rem 0;
  border: none;
  border-radius: 0 0 0.25rem 0.25rem;

  &:disabled {
    cursor: auto;
    opacity: 30%;
  }
`;

type Option = {
  name: string;
  checked: boolean;
  label?: string;
};

export type Props = {
  title: string;
  options: Option[];
  applyButtonDisabled: boolean;
  onCheckboxChange: (value: string) => void;
  onApply: () => void;
  onClose?: () => void;
};

export default function MultipleSelectFilter({
  title,
  options,
  applyButtonDisabled,
  onCheckboxChange,
  onApply,
  onClose,
}: Props): ReactElement<any> {
  const ref = useRef<HTMLDivElement>(null);
  const [buttonTitle, setButtonTitle] = useState(() => {
    const optionsSelectedLength = options.filter((i) => i.checked === true)?.length;
    if (optionsSelectedLength > 0) {
      return `${optionsSelectedLength} selected`;
    } else {
      return title;
    }
  });
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const toggleForm = () => {
    setIsOpen((prev) => {
      // Run onclose function on closing the form
      if (onClose && prev) onClose();
      return !prev;
    });
  };
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    onApply();
    setIsOpen(false);
  };

  /**
   * Closing filter when
   * click outside of it
   */
  const handleClickOutside = (event: Event) => {
    if (ref.current && !ref.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };
  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  });


  const buttonId = `${title.replace(/ /g, '')}button`;
  const formId = `${title.replace(/ /g, '')}form`;
  return (
    <div ref={ref}>
      <StyledToggleButton
        onClick={toggleForm}
        aria-expanded={isOpen ? 'true' : 'false'}
        aria-controls={formId}
        id={buttonId}
        isOpen={isOpen}
      >
        {buttonTitle}
      </StyledToggleButton>
      <StyledForm id={formId} aria-labelledby={buttonId} isOpen={isOpen} aria-hidden={!isOpen}>
        {options.map((option) => (
          <StyledLabel key={option.name}>
            <StyledCheckbox
              type="checkbox"
              onChange={() => onCheckboxChange(option.name)}
              checked={option.checked}
            />
            {option.label ? option.label : option.name}
          </StyledLabel>
        ))}
        <StyledApplyButton type="submit" onClick={onSubmit} disabled={applyButtonDisabled}>
          apply filters
        </StyledApplyButton>
      </StyledForm>
    </div>
  );
}
